// 207C §2 [RULED — Laurent, 2026-09-24, ruling 6] — THE AMOUNT FIELD: an
// amount shows its thousands with a space and is typed with a decimal comma.
//
// Moved out of the case form's rate card first, unchanged (the previous
// commit: `rcI` and its `parseN`, CaseModal.tsx:431-499@0f3a86b5 — the one
// input in the tree that already read and showed an amount this way, which the
// brief says the quantum takes), then WIDENED here for the three places a
// quantum is typed — the collective case's members list, its « new client »
// dialog, and the client form — to the union of what the four call sites pass
// (THE FOUR RULES, rules 2 and 4): the rate card's unit, and the quantum's
// class, style, placeholder, lock, keypad, and what an empty amount shows.
//
// THE RULE (the parse is src/lib/amountText.ts, where the recording runs it):
//   · not being typed in, the field shows the figure `fq` prints — « 92 159,50 »
//     — and, when nothing is stored, `empty`: « — » by default (the rate card's
//     served figures before they land), nothing for a quantum (ruling 5: an
//     empty cell, its amber border says so);
//   · on focus it shows the number to edit, with its decimal comma
//     (« 92159,5 »; nothing when nothing is stored), tinted while typed in, and
//     WHAT IS TYPED STAYS AS TYPED until the field is left;
//   · a key is kept only if the text still reads as an amount — digits and ONE
//     decimal separator, the comma or a dot read as it, spaces ignored — so a
//     letter or a second separator is refused as it is typed, as the list's
//     cell refused them (the rate card's `parseN` stripped them silently, and
//     read « 1.234,56 » as 1.234);
//   · `onAmount` is handed the number, or null when nothing is typed; the host
//     saves it when it saved it before — the list at once, through 207B §5's
//     queue; a dialog on its button; the rate card ignores null, as it ignored
//     an unparsable text.
//
// 208 §10 — EVERY AMOUNT IS TYPED THE WAY THE QUANTUM IS: the inputs 207C §2's
// census listed take this field, and it WIDENS to the union of what they pass
// (rule 2): `disabled` (a read-only calculator, a cleared bulk rate), `title`,
// `autoFocus` (a payment's dialog), `max` — the ceiling a typed amount is held
// at, shown at once, as the pro forma's `NumField` clamped a source's draw and
// a refund (161B §2) — and `onLeave`, handed the number when the field is left
// after typing, for a host that saves on leaving (the rates panel's rows).
import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties, Dispatch, ReactNode } from "react";
import { FS } from "../lib/tokens";
import { fq } from "../utils";
import { amountKeystroke, amountValue, parseAmount } from "../lib/amountText";

interface AmountFieldProps {
  value: unknown;
  onAmount: Dispatch<number | null>;
  suffix?: ReactNode;
  empty?: string;
  className?: string;
  style?: CSSProperties;
  placeholder?: string;
  readOnly?: boolean;
  inputMode?: "decimal";
  disabled?: boolean;
  title?: string;
  autoFocus?: boolean;
  /** The ceiling a typed amount is held at — the text shows it at once. */
  max?: number;
  /** Handed the typed number when the field is left after typing. */
  onLeave?: Dispatch<number | null>;
  /** 216 addendum 6 §10 (4) [RULED 82] — a leading minus may be typed: the
      document is a reimbursement (the payment window alone passes it). */
  negative?: boolean;
}

export default function AmountField({
  value,
  onAmount,
  suffix,
  empty = fq(null),
  className = "field-input",
  style,
  placeholder,
  readOnly,
  inputMode,
  disabled,
  title,
  autoFocus,
  max,
  onLeave,
  negative = false,
}: Readonly<AmountFieldProps>) {
  const [editing, setEditing] = useState<string | null>(null);
  // `autoFocus`: focused once it is drawn, as the attribute would (a payment's dialog opens on its amount)
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  const active = editing !== null;
  const stored = amountValue(value);
  const raw = Math.round((stored ?? 0) * 100) / 100;
  const idle = stored === null ? empty : fq(raw);
  const disp = editing ?? idle;
  const input = (
    <input
      type="text"
      className={className}
      inputMode={inputMode}
      placeholder={placeholder}
      readOnly={readOnly}
      ref={ref}
      disabled={disabled}
      title={title}
      value={disp}
      style={{
        background: active ? "var(--as)" : "var(--bi)",
        ...style,
      }}
      onFocus={() => {
        if (!readOnly && !disabled) setEditing(stored === null ? "" : String(raw).replace(".", ","));
      }}
      onBlur={() => {
        if (editing !== null && onLeave) onLeave(parseAmount(editing, negative));
        setEditing(null);
      }}
      onChange={(e) => {
        // the keystroke's rule, and a ceiling's (src/lib/amountText.ts, where the recordings run it)
        const k = amountKeystroke(e.target.value, max, negative);
        if (!k) return;
        setEditing(k.text);
        onAmount(k.amount);
      }}
    />
  );
  if (suffix === undefined) return input;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {input}
      <span
        style={{
          fontSize: FS.s10,
          color: "var(--tm)",
          whiteSpace: "nowrap",
        }}
      >
        {suffix}
      </span>
    </div>
  );
}
